import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Check,
  Eye,
  FileText,
  KeyRound,
  Loader2,
  Search,
  Shield,
  Trash2,
  User,
  Users,
} from "lucide-react";

import { NGROK } from "../../../APIs";
import { myLocalStorage } from "../../../components/StorageHelper";
import useUserStore from "../../../services/userStore";
import useNotificationStore from "../../../services/notificationStore";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import useCloudSyncWatcher from "../../../services/useCloudSyncWatcher";
import { triggerCloudSync } from "../../../triggerCloudSync";
import CloudFilters from "../../../components/v2components/CloudFilters";
import PolicyDetailsModal from "./components/PolicyDetailsModal";
import { parseAwsPolicyDocument } from "./utils/parseAwsPolicyDocument";

const emptyArray = [];

const getPolicyName = (policy) => {
  const raw = policy?.policyARN || policy?.policyArn || policy || "";
  return raw?.split("/")?.pop() || raw || "-";
};

const getPolicyArn = (policy) => policy?.policyARN || policy?.policyArn || policy || "";

const getStoredTenantName = () => {
  const stored = myLocalStorage.getItem("latestTenant");
  return typeof stored === "string" ? stored : stored?.tenantName || "";
};

const DetailRow = ({ label, value }) => (
  <div className="rounded-2xl border border-border bg-muted/20 p-3">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 break-words text-sm font-medium text-foreground">{value || "-"}</div>
  </div>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex min-h-[240px] flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-8 text-center shadow-lg shadow-sky-500/5">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/60">
      <Icon className="h-7 w-7 text-muted-foreground" />
    </div>
    <h3 className="text-base font-semibold text-foreground">{title}</h3>
    <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
  </div>
);

const SkeletonList = () => (
  <div className="space-y-3">
    {[1, 2, 3, 4, 5].map((item) => (
      <div key={item} className="animate-pulse rounded-2xl border border-border bg-card p-4 shadow-lg shadow-sky-500/5">
        <div className="mb-3 h-4 w-1/2 rounded bg-muted/60" />
        <div className="h-3 w-3/4 rounded bg-muted/40" />
      </div>
    ))}
  </div>
);

const gridClass = "grid grid-cols-1 gap-5 xl:grid-cols-3";
const panelClass = "flex min-h-[calc(100vh-250px)] flex-col overflow-hidden rounded-2xl border portal-card-elevated bg-card p-4";

const AWSGroups = () => {
  const userData = useUserStore((state) => state.user);
  const syncStatus = useCloudSyncStore((state) => state.syncStatus);
  const { awsConfig } = useNotificationStore.getState();

  const [selectedTenant, setSelectedTenant] = useState(getStoredTenantName());
  const [selectedAccount, setSelectedAccount] = useState(myLocalStorage.getItem("selectedAwsAccount") || "");
  const [searchTerm, setSearchTerm] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMemberName, setSelectedMemberName] = useState("");
  const [activeTab, setActiveTab] = useState("user");
  const [loading, setLoading] = useState(true);
  const [loadingPolicies, setLoadingPolicies] = useState({});
  const [policiesDetailsModalOpen, setPoliciesDetailsModalOpen] = useState(false);
  const [policiesDetails, setPoliciesDetails] = useState([]);

  const fetchPolicyDocDetails = async (policyArn) => {
    const response = await axios.get(
      `${NGROK}/api/aws/getSpecificIAMPolicydetails`,
      {
        params: {
          tenantName: selectedTenant,
          policyarn: policyArn,
          accountId: selectedAccount,
        },
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      },
    );
    return response.data;
  };

  const handleViewPolicy = async (policyArn) => {
    if (!policyArn) return;
    setLoadingPolicies((prev) => ({ ...prev, [policyArn]: true }));
    try {
      const policyDetails = await fetchPolicyDocDetails(policyArn);
      if (policyDetails?.policyDocument) {
        policyDetails.policyDocument = parseAwsPolicyDocument(policyDetails.policyDocument);
      }
      setPoliciesDetails([policyDetails]);
      setPoliciesDetailsModalOpen(true);
    } catch (error) {
      console.error("Error fetching policy details:", error);
    } finally {
      setLoadingPolicies((prev) => ({ ...prev, [policyArn]: false }));
    }
  };

  const combinePermissionsAndRoles = (groupData, rolesData) =>
    groupData.map((group) => {
      const policyArns = [
        ...(group.groupPolicies || []).map(getPolicyArn),
        ...(group.userPolicies || []).map(getPolicyArn),
      ].filter(Boolean);

      const roles = [];
      rolesData.forEach((role) => {
        role?.rolePolicies?.forEach((policy) => {
          if (policyArns.includes(policy.policyArn)) {
            roles.push({
              roleName: role.roleName,
              roleId: role.roleId,
              roleArn: role.roleArn,
            });
          }
        });
      });

      return { ...group, roles };
    });

  const fetchData = useCallback(async (initialLoad = false) => {
    if (!selectedTenant || !selectedAccount) {
      setGroups([]);
      setSelectedGroup(null);
      setSelectedUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [groupsResponse, rolesResponse] = await Promise.all([
        axios.get(`${NGROK}/api/aws/getIamgroups`, {
          params: { tenantName: selectedTenant, accountId: selectedAccount },
        }),
        axios.get(`${NGROK}/api/aws/getIamroles`, {
          params: { tenantName: selectedTenant, accountId: selectedAccount },
        }),
      ]);

      const groupData = combinePermissionsAndRoles(groupsResponse.data || [], rolesResponse.data || []);
      setGroups(groupData);

      setSelectedGroup((current) => {
        if (!groupData.length) return null;
        const existing = current ? groupData.find((group) => group.arn === current.arn || group.groupName === current.groupName) : null;
        return existing || (initialLoad ? groupData[0] : groupData[0]);
      });
    } catch (error) {
      console.error("Error fetching AWS groups:", error);
      setGroups([]);
      setSelectedGroup(null);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, selectedTenant]);

  const handleSync = async () => {
    try {
      await triggerCloudSync("aws", "/api/aws/ondemand", selectedTenant, () => fetchData(false));
    } catch (error) {
      console.error("AWS sync failed:", error);
    }
  };

  useCloudSyncWatcher("aws", selectedTenant, () => fetchData(false));

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    setSelectedUser(null);
    setSelectedMemberName("");
    setMemberSearch("");
  }, [selectedGroup]);

  const filteredGroups = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return groups.filter((group) => group.groupName?.toLowerCase().includes(query));
  }, [groups, searchTerm]);

  const groupUsers = selectedGroup?.users || emptyArray;
  const filteredUsers = useMemo(() => {
    const query = memberSearch.toLowerCase();
    return groupUsers.filter((user) => user.username?.toLowerCase().includes(query) || user.arn?.toLowerCase().includes(query));
  }, [groupUsers, memberSearch]);

  const groupPolicies = selectedGroup?.groupPolicies || emptyArray;
  const groupRoles = selectedGroup?.roles || emptyArray;

  const handleDeleteMembership = async () => {
    if (!selectedGroup || !selectedMemberName) return;
    try {
      await axios.post(
        `${NGROK}/api/aws/removeUserGroup`,
        selectedMemberName,
        {
          params: {
            tenantName: selectedTenant,
            groupName: selectedGroup.groupName,
            accountId: selectedAccount,
          },
        },
      );
      setSelectedMemberName("");
      setSelectedUser(null);
      await triggerCloudSync("aws", "/api/aws/ondemand", selectedTenant, () => fetchData(false));
    } catch (error) {
      console.error("Failed to delete group membership:", error);
    }
  };

  const handleTenantChange = (tenant) => {
    if (tenant === selectedTenant) return;
    setSelectedTenant(tenant);
    setSelectedGroup(null);
    setSelectedUser(null);
    setGroups([]);
  };

  const handleAccountChange = (account) => {
    if (account === selectedAccount) return;
    setSelectedAccount(account);
    setSelectedGroup(null);
    setSelectedUser(null);
    setGroups([]);
  };

  return (
    <div className="h-full bg-background p-6">
      <CloudFilters
        cloudProvider="aws"
        onTenantChange={handleTenantChange}
        onCloudAccountChange={handleAccountChange}
        isAdmin={userData.role !== "TENANT_USER"}
        searchQuery={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search groups..."
        onSync={handleSync}
        syncDisabled={!awsConfig || syncStatus.aws || !selectedTenant || !selectedAccount}
        syncLoading={Boolean(selectedAccount) && syncStatus.aws}
      />

      <div className={gridClass}>
        <section className={panelClass}>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-foreground">Groups</h2>
              <p className="text-xs text-muted-foreground">{filteredGroups.length} of {groups.length} groups</p>
            </div>
            <div className="rounded-xl bg-primary/10 p-2 text-primary">
              <Users className="h-4 w-4" />
            </div>
          </div>

          {loading ? (
            <SkeletonList />
          ) : filteredGroups.length === 0 ? (
            <EmptyState icon={Users} title="No AWS groups found" description="No groups match this tenant, account, or search." />
          ) : (
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1 scrollbar-thin">
              {filteredGroups.map((group) => {
                const active = selectedGroup?.arn === group.arn || selectedGroup?.groupName === group.groupName;
                return (
                  <button
                    key={group.arn || group.groupName}
                    onClick={() => setSelectedGroup(group)}
                    className={`w-full rounded-2xl border p-4 text-left portal-card-hover ${
                      active ? "border-primary/40 bg-primary/5 shadow-lg shadow-sky-500/10" : "border-border bg-background"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-foreground">{group.groupName}</div>
                        <div className="mt-1 truncate text-xs text-muted-foreground">{group.arn || "No ARN available"}</div>
                      </div>
                      {active && <Check className="h-4 w-4 flex-shrink-0 text-primary" />}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full bg-blue-500/10 px-2.5 py-1 text-xs font-medium text-blue-600">{group.users?.length || 0} users</span>
                      <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600">{group.groupPolicies?.length || 0} policies</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className={panelClass}>
          {!selectedGroup ? (
            <EmptyState icon={Shield} title="Select a group" description="Choose an AWS group to inspect members, roles, and permissions." />
          ) : (
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{selectedGroup.groupName}</h2>
                  <p className="mt-1 break-all text-xs text-muted-foreground">{selectedGroup.arn}</p>
                </div>
                <button
                  onClick={handleDeleteMembership}
                  disabled={!selectedMemberName}
                  className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all ${
                    selectedMemberName
                      ? "bg-destructive text-destructive-foreground shadow-lg shadow-destructive/20 hover:-translate-y-px"
                      : "cursor-not-allowed bg-muted text-muted-foreground"
                  }`}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Membership
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <DetailRow label="Users" value={groupUsers.length} />
                <DetailRow label="Roles" value={groupRoles.length} />
                <DetailRow label="Policies" value={groupPolicies.length} />
              </div>

              <div className="rounded-2xl border border-border bg-background p-3">
                <div className="mb-3 flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <input
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    placeholder="Search members..."
                    value={memberSearch}
                    onChange={(event) => setMemberSearch(event.target.value)}
                  />
                </div>

                <div className="max-h-[280px] space-y-2 overflow-y-auto pr-1 scrollbar-thin">
                  {filteredUsers.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No members found</div>
                  ) : (
                    filteredUsers.map((user) => {
                      const active = selectedUser?.username === user.username;
                      const checked = selectedMemberName === user.username;
                      return (
                        <button
                          key={user.arn || user.username}
                          onClick={() => {
                            setSelectedUser(user);
                            setActiveTab("user");
                          }}
                          className={`w-full rounded-2xl border p-3 text-left portal-card-hover ${
                            active ? "border-primary/40 bg-primary/5" : "border-border bg-card"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedMemberName(checked ? "" : user.username);
                              }}
                              className={`flex h-5 w-5 items-center justify-center rounded-md border ${
                                checked ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"
                              }`}
                            >
                              {checked && <Check className="h-3 w-3" />}
                            </span>
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600">
                              <User className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-foreground">{user.username}</div>
                              <div className="truncate text-xs text-muted-foreground">{user.arn}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-background p-4">
                <div className="mb-4 flex flex-wrap gap-2">
                  {[
                    { id: "user", label: "User", icon: User },
                    { id: "roles", label: `Roles (${groupRoles.length})`, icon: Shield },
                    { id: "permissions", label: `Permissions (${groupPolicies.length})`, icon: KeyRound },
                  ].map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-all ${
                          activeTab === tab.id ? "bg-primary text-primary-foreground shadow-md shadow-primary/20" : "bg-card text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {activeTab === "user" && (
                  selectedUser ? (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <DetailRow label="Username" value={selectedUser.username} />
                      <DetailRow label="User ID" value={selectedUser.userId} />
                      <DetailRow label="Created" value={selectedUser.createDate ? moment(selectedUser.createDate).format("DD/MM/YYYY HH:mm:ss") : "-"} />
                      <DetailRow label="ARN" value={selectedUser.arn} />
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Select a member to see user details.</div>
                  )
                )}

                {activeTab === "roles" && (
                  <div className="space-y-2">
                    {groupRoles.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No roles linked through this group.</div>
                    ) : groupRoles.map((role) => (
                      <div key={role.roleArn || role.roleName} className="rounded-2xl border border-border bg-card p-3 portal-card-hover">
                        <div className="text-sm font-semibold text-foreground">{role.roleName}</div>
                        <div className="mt-1 break-all text-xs text-muted-foreground">{role.roleArn}</div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === "permissions" && (
                  <div className="space-y-2">
                    {groupPolicies.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No group policies assigned.</div>
                    ) : groupPolicies.map((policy) => {
                      const arn = getPolicyArn(policy);
                      return (
                        <div key={arn} className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3 portal-card-hover">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-foreground">{getPolicyName(policy)}</div>
                            <div className="truncate text-xs text-muted-foreground">{arn}</div>
                          </div>
                          <button
                            onClick={() => handleViewPolicy(arn)}
                            disabled={loadingPolicies[arn]}
                            className="inline-flex items-center gap-2 rounded-xl border border-primary/20 px-3 py-2 text-xs font-semibold text-primary transition-all hover:bg-primary hover:text-primary-foreground disabled:opacity-60"
                          >
                            {loadingPolicies[arn] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                            View
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        <section className={panelClass}>
          {selectedUser ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-600">
                  <User className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h2 className="truncate text-base font-semibold text-foreground">{selectedUser.username}</h2>
                  <p className="truncate text-xs text-muted-foreground">{selectedUser.arn}</p>
                </div>
              </div>
              <DetailRow label="User ID" value={selectedUser.userId} />
              <DetailRow label="Created At" value={selectedUser.createDate ? moment(selectedUser.createDate).format("DD/MM/YYYY HH:mm:ss") : "-"} />
              <DetailRow label="Group" value={selectedGroup?.groupName} />
            </div>
          ) : (
            <EmptyState icon={FileText} title="No member selected" description="Select a group member to view their AWS identity summary." />
          )}
        </section>
      </div>

      <PolicyDetailsModal
        open={policiesDetailsModalOpen}
        onClose={() => setPoliciesDetailsModalOpen(false)}
        policiesDetails={policiesDetails}
      />
    </div>
  );
};

export default AWSGroups;
